import React, { useState, useEffect } from 'react';
import { standingsManager } from '../services/api/managers';
import Header from '../components/Header';
import '../styles/index.css';

const LEAGUES = [
  { id: 39, name: 'Premier League' },
  { id: 140, name: 'La Liga' },
  { id: 78, name: 'Bundesliga' }
];

// Predefined seasons
const AVAILABLE_SEASONS = [2024, 2023, 2022, 2021, 2020];

const StandingsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Selected season and league
  const [season, setSeason] = useState(2024);
  const [selectedLeague, setSelectedLeague] = useState(LEAGUES[0].id);

  const [viewMode, setViewMode] = useState('normal');
  
  // Check screen size on load
  useEffect(() => {
    const checkScreenSize = () => {
      if (window.innerWidth <= 768) {
        setViewMode('compact');
      } else {
        setViewMode('normal');
      }
    };
    
    // Check on load
    checkScreenSize();
    
    // Add resize event listener
    window.addEventListener('resize', checkScreenSize);
    
    // Clean up listener on unmount
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Season change handler
  const handleSeasonChange = (e) => {
    setSeason(Number(e.target.value));
  };

  // League change handler
  const handleLeagueChange = (e) => {
    setSelectedLeague(Number(e.target.value));
  };
  
  // Load standings when season or league changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const selectedLeagueName = LEAGUES.find(league => league.id === selectedLeague)?.name || 'Selected League';
        console.log(`Loading standings for ${selectedLeagueName}, season ${season}`);
        const response = await standingsManager.fetchStanding(selectedLeague, season);
        
        if (response.status === 'success' && response.data) {
          setData(response.data);
        } else {
          setError(response.message || 'Error loading data');
          setData(null);
        }
      } catch (err) {
        console.error('Error loading standings:', err);
        setError('Error loading data: ' + (err.message || 'Unknown error'));
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [season, selectedLeague]);

  // Render table in normal mode
  const renderNormalTable = (standings, leagueData, leagueName) => {
    const displaySeason = `${season}/${season + 1}`;
    return (
      <>
        <div className="league-info">
          <h2>{leagueName} - Season {displaySeason}</h2>
        </div>
        
        <div className="standings-table" style={{ 
          width: '100%',
          border: '1px solid #eee',
          borderRadius: '8px',
          position: 'relative'
        }}>          
          {/* Teams rows */}
          {standings.map((team, index) => (
            <div key={team.team.id || `team-${Math.random()}`} className="team-row" style={{
              padding: '8px 0',
              borderBottom: index < standings.length - 1 ? '1px solid #eee' : 'none',
              backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <div className="team-info" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                flex: '1',
                justifyContent: 'center'
              }}>
                <span className="team-rank" style={{ 
                  minWidth: '20px', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  marginRight: '5px',
                  fontSize: '14px'
                }}>
                  {team.rank}
                </span>
                {team.team.logo && (
                  <img 
                    src={team.team.logo} 
                    alt={team.team.name} 
                    className="team-icon"
                    style={{ minWidth: '25px', height: '25px', marginRight: '8px' }}
                    onError={(e) => {e.target.onerror = null; e.target.src = 'https://via.placeholder.com/30?text=No+Logo'}}
                  />
                )}
                <span className="team-name" style={{ 
                  fontSize: '14px',
                  whiteSpace: 'normal', 
                  overflow: 'visible',
                  textOverflow: 'clip',
                  lineHeight: '1.2',
                  flex: '1',
                  textAlign: 'center'
                }}>
                  {team.team.name}
                </span>
              </div>
              <div className="team-stats" style={{ 
                display: 'flex', 
                width: '160px',
                justifyContent: 'space-between',
                paddingRight: '20px'
              }}>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render table in compact mode for mobile devices
  const renderCompactTable = (standings, leagueData, leagueName) => {
    const displaySeason = `${season}/${season + 1}`;
    return (
      <>
        <div className="league-info">
          <h2>{leagueName} - Season {displaySeason}</h2>
        </div>
        
        <div className="standings-table-compact" style={{ 
          width: '100%',
          border: '1px solid #eee',
          borderRadius: '8px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Table header */}
          <div className="table-header" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            borderBottom: '2px solid #ddd',
            padding: '6px 0',
            fontWeight: 'bold',
            fontSize: '12px',
            position: 'sticky',
            top: 0,
            backgroundColor: '#f8f9fa',
            zIndex: 1
          }}>
            <div style={{ flex: '1', paddingLeft: '28px' }}>Team</div>
            <div style={{ 
              display: 'flex', 
              width: '100px',
              justifyContent: 'space-between',
              paddingRight: '10px'
            }}>
              {/* <div style={{ width: '20px', textAlign: 'center' }}>W</div>
              <div style={{ width: '20px', textAlign: 'center' }}>D</div>
              <div style={{ width: '20px', textAlign: 'center' }}>L</div>
              <div style={{ width: '30px', textAlign: 'center' }}>PTS</div> */}
            </div>
          </div>
        
          {/* Teams rows */}
          {standings.map((team, index) => (
            <div key={team.team.id || `team-${Math.random()}`} className="team-row" style={{
              padding: '8px 0',
              borderBottom: index < standings.length - 1 ? '1px solid #eee' : 'none',
              backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%'
            }}>
              <div className="team-info" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                flex: '1',
                justifyContent: 'center'
              }}>
                <span className="team-rank" style={{ 
                  minWidth: '20px', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  marginRight: '5px',
                  fontSize: '14px'
                }}>
                  {team.rank}
                </span>
                {team.team.logo && (
                  <img 
                    src={team.team.logo} 
                    alt={team.team.name} 
                    className="team-icon"
                    style={{ minWidth: '25px', height: '25px', marginRight: '8px' }}
                    onError={(e) => {e.target.onerror = null; e.target.src = 'https://via.placeholder.com/30?text=No+Logo'}}
                  />
                )}
                <span className="team-name" style={{ 
                  fontSize: '14px',
                  whiteSpace: 'normal', 
                  overflow: 'visible',
                  textOverflow: 'clip',
                  lineHeight: '1.2',
                  flex: '1',
                  textAlign: 'center'
                }}>
                  {team.team.name}
                </span>
              </div>
              <div className="team-stats" style={{ 
                display: 'flex', 
                width: '100px',
                justifyContent: 'space-between',
                paddingRight: '10px',
                alignItems: 'left'
              }}>
                {/* <div className="stat-item" style={{ width: '20px', textAlign: 'center', fontSize: '12px' }}>
                  {team.all.win}
                </div>
                <div className="stat-item" style={{ width: '20px', textAlign: 'center', fontSize: '12px' }}>
                  {team.all.draw}
                </div>
                <div className="stat-item" style={{ width: '20px', textAlign: 'center', fontSize: '12px' }}>
                  {team.all.lose}
                </div>
                <div className="stat-item points" style={{ width: '30px', textAlign: 'center', fontWeight: 'bold', fontSize: '12px' }}>
                  {team.points}
                </div> */}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderStandings = () => {
    console.log('Rendering standings with data:', data);
    
    if (!data || !data.response || data.response.length === 0) {
      return <div className="empty-data">No standings data available for selected season and league</div>;
    }

    const leagueData = data.response[0]?.league;
    if (!leagueData || !leagueData.standings || leagueData.standings.length === 0) {
      return <div className="empty-data">Standings are empty</div>;
    }
    
    const standings = leagueData.standings[0];
    if (!standings || !Array.isArray(standings) || standings.length === 0) {
      return <div className="empty-data">Standings contain no teams</div>;
    }
    
    // Find selected league name
    const leagueName = LEAGUES.find(league => league.id === selectedLeague)?.name || 'Selected League';
    
    // Use appropriate render based on display mode
    return viewMode === 'normal' 
      ? renderNormalTable(standings, leagueData, leagueName)
      : renderCompactTable(standings, leagueData, leagueName);
  };

  return (
    <div className="data-page">
      <Header title="Standings Page" />
      <div className="content-container">
        <div className="controls">
          <div className="select-container">
            <label htmlFor="league-select">League:</label>
            <select
              id="league-select"
              value={selectedLeague}
              onChange={handleLeagueChange}
              disabled={loading}
              style={{ marginRight: '15px' }}
            >
              {LEAGUES.map(league => (
                <option key={league.id} value={league.id}>
                  {league.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="select-container">
            <label htmlFor="season-select">Season:</label>
            <select
              id="season-select"
              value={season}
              onChange={handleSeasonChange}
              disabled={loading}
            >
              {AVAILABLE_SEASONS.map(season => (
                <option key={season} value={season}>
                  {`${season}/${season + 1}`}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading standings...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          renderStandings()
        )}
      </div>
    </div>
  );
};

export default StandingsPage;